import {
  CloseOutlined,
  CheckOutlined,
  EyeOutlined,
  RollbackOutlined,
} from "@ant-design/icons";

import styles from "./stylesViewDetailsModal.module.css";

import { formatCurrency } from "../../../../utils/financialFormatters";
import { useWithdrawalReview } from "../WithdrawalReviewContext";
import { convertToUserTimezone } from "../../../../utils/dateService";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { snakeCaseToTitleLabel } from "../../../../utils/UserUtils";

const blacklist = ["'s checking", "'s savings"];

function ViewDetailsModal({
  canRefundWithdrawal = false,
  currencySymbol,
}) {
  const {
    selectedTab,
    userDetails,
    setIsDetailsModalOpen,
    handleViewUser,
    openApproveModal,
    openDeclinedModal,
    openRefundModal,
    approveTransactionLoading,
    declineTransactionLoading,
    refundTransactionLoading,
  } = useWithdrawalReview();

  const handleClose = () => {
    setIsDetailsModalOpen(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "declined":
      case "cancelled":
        return styles.red;
      case "in_progress":
      case "pending_review":
      case "refund":
        return styles.orange;
      case "approved":
      case "completed":
        return styles.green;
      default:
        return styles.green;
    }
  };

  const cleanKycName = (name) => {
    if (!name) {
      return "";
    }

    let result = name.toLowerCase();
    blacklist.forEach((word) => {
      const regex = new RegExp(word, "gi");
      result = result.replace(regex, "");
    });
    return result.trim();
  };

  const handleApproveRequest = (record) => {
    handleClose();
    openApproveModal(record);
  };

  const handleDeclineRequest = (record) => {
    handleClose();
    openDeclinedModal(record);
  };

  const handleRefundRequest = (record) => {
    handleClose();
    openRefundModal(record);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <h2>{AppStrings.MODAL_TITLE}</h2>
        </div>
        <CloseOutlined onClick={handleClose} className={styles.closeIcon} />
      </div>

      <div className={styles.content}>
        <div className={styles.details}>
          <span className={styles.label}>{AppStrings.REQUEST_DATE}</span>
          <span>
            {convertToUserTimezone(userDetails?.request_date_timestamp)}
          </span>
        </div>
        {selectedTab === "PROCESSED" && (
          <div className={styles.details}>
            <span className={styles.label}>{AppStrings.PROCESSED_DATE}</span>
            <span>{convertToUserTimezone(userDetails?.reviewed_at)}</span>
          </div>
        )}

        <div className={styles.details}>
          <span className={styles.label}>{AppStrings.USERNAME}</span>
          <button
            type="button"
            className={styles.usernameLink}
            onClick={() => handleViewUser(userDetails)}
          >
            {userDetails?.username}
          </button>
        </div>
        <div className={styles.details}>
          <span className={styles.label}>{AppStrings.TRANSACTION_ID}</span>
          <span style={{ maxWidth: 250 }}>{userDetails?.transaction_id}</span>
        </div>
        <div className={styles.details}>
          <span className={styles.label}>
            {AppStrings.LAST_DEPOSIT_AMOUNT}
          </span>
          <span>
            {formatCurrency(
              Number(userDetails?.last_deposit_amount ?? 0),
              currencySymbol,
              2,
            )}
          </span>
        </div>
        <div className={styles.details}>
          <span className={styles.label}>
            {AppStrings.WITHDRAWAL_AMOUNT}
          </span>
          <span>
            {formatCurrency(
              Number(userDetails?.withdrawal_amount ?? 0),
              currencySymbol,
              2,
            )}
          </span>
        </div>
        <div className={styles.details}>
          <span className={styles.label}>{AppStrings.PAYMENT_GATEWAY}</span>
          <span>{userDetails?.payment_gateway}</span>
        </div>
        <div className={styles.details}>
          <span className={styles.label}>{AppStrings.DECISION}</span>
          {selectedTab === "PENDING" ? (
            <span className={`${styles.status} ${styles.orange}`}>
              {AppStrings.PENDING}
            </span>
          ) : (
            <span
              className={`${styles.status} ${getStatusColor(
                userDetails?.transaction_status?.toLowerCase(),
              )}`}
            >
              {snakeCaseToTitleLabel(userDetails?.transaction_status)}
            </span>
          )}
        </div>
        <div className={styles.details}>
          <span className={styles.label}>{AppStrings.KYC_NAME}</span>
          <span>
            {cleanKycName(userDetails?.kyc_cardholder_name)?.toUpperCase()}
          </span>
        </div>
        <div className={styles.details}>
          <span className={styles.label}>{AppStrings.CARDHOLDER_NAME}</span>
          <span>{userDetails?.full_name?.toUpperCase()}</span>
        </div>
        <div className={styles.details}>
          <span className={styles.label}>{AppStrings.CARD_NUMBER}</span>
          <span>**** **** **** {userDetails?.card_last_four}</span>
        </div>

        {selectedTab === "PROCESSED" && (
          <div className={styles.details}>
            <span className={styles.label}>{AppStrings.PROCESSED_BY}</span>
            <span>{userDetails?.reviewed_by}</span>
          </div>
        )}

        {selectedTab === "PROCESSED" &&
          userDetails.transaction_status === "declined" && (
            <div className={styles.warningContainer}>
              <p>{snakeCaseToTitleLabel(userDetails.review_reason)}</p>
            </div>
          )}
      </div>

      <div className={styles.divider} />

      <div className={styles.footer}>
        {selectedTab === "PENDING" || selectedTab === "IN_PROGRESS" ? (
          <div className={styles.buttons}>
            <button
              className={`${styles.button} ${styles.acceptButton}`}
              onClick={() => handleApproveRequest(userDetails)}
              disabled={
                approveTransactionLoading ||
                declineTransactionLoading ||
                refundTransactionLoading
              }
            >
              <CheckOutlined />
              <span>{AppStrings.ACCEPT}</span>
            </button>
            <button
              className={`${styles.button} ${styles.declineButton}`}
              onClick={() => handleDeclineRequest(userDetails)}
              disabled={
                approveTransactionLoading ||
                declineTransactionLoading ||
                refundTransactionLoading
              }
            >
              <CloseOutlined />
              <span>{AppStrings.DECLINE}</span>
            </button>
            {canRefundWithdrawal && (
              <button
                className={`${styles.button} ${styles.refundButton}`}
                onClick={() => handleRefundRequest(userDetails)}
                disabled={
                  approveTransactionLoading ||
                  declineTransactionLoading ||
                  refundTransactionLoading
                }
              >
                <RollbackOutlined />
                <span>{AppStrings.WITHDRAWAL_REFUND_ACTION}</span>
              </button>
            )}
          </div>
        ) : (
          <div className={styles.closeButton}>
            <button onClick={handleClose}>
              <span>{AppStrings.CLOSE}</span>
            </button>
          </div>
        )}
        <div className={styles.checkUserContainer}>
          <button onClick={() => handleViewUser(userDetails)}>
            <EyeOutlined style={{ marginRight: 6 }} />
            <span>{AppStrings.CHECK_USERS_PROFILE}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default ViewDetailsModal;
